import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Typography,
  Chip,
  Divider,
  Avatar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Stack,
} from "@mui/material";
import PreviewIcon from "@mui/icons-material/Preview";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InventoryIcon from "@mui/icons-material/Inventory2Rounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PublishedWithChangesRoundedIcon from "@mui/icons-material/PublishedWithChangesRounded";
import UnpublishedRoundedIcon from "@mui/icons-material/UnpublishedRounded";
import { Link, useNavigate } from "react-router-dom";
import IProduct from "../../entities/IProduct";
import ProductService from "../../services/ProductService";
import ProductSkuService from "../../services/ProductSkuService";
import { getErrorMessage, unwrapData } from "../../services/apiUtils";
import {
  isVariableProductType,
  extractCategoryId,
  buildOptionValueLookup,
  buildSkuCombo,
} from "../../services/productTypeUtils";
import { toPlainText } from "../../services/textUtils";
import { theme } from "../../theme";
import { isProductPublished, PRODUCT_STATUS } from "../../services/productStatus";
import productEvents from "../../services/productEvents";

type ProductActionsProps = {
  product: IProduct;
  onDeleted?: () => void;
};

const ProductActions: React.FC<ProductActionsProps> = ({ product, onDeleted }) => {
  const navigate = useNavigate();
  const [viewOpen, setViewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [unpublishToEditOpen, setUnpublishToEditOpen] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const isPublished = isProductPublished(product);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  type SkuPreviewRow = {
    skuId: number;
    skuCode: string;
    price: number;
    stock: number;
    isActive: boolean;
    combo: string;
  };
  const [skuRows, setSkuRows] = useState<Array<SkuPreviewRow>>([]);
  const [skuLoading, setSkuLoading] = useState(false);
  const [skuLoadError, setSkuLoadError] = useState(false);

  const isVariable = isVariableProductType(product.product_type);

  const loadSkus = useCallback(() => {
    const categoryId = extractCategoryId(product);
    if (!isVariable || !categoryId || !product.productId) return;
    setSkuLoading(true);
    setSkuLoadError(false);
    ProductSkuService.getProductWithCategoryVariant(categoryId, product.productId)
      .then((res: any) => {
        const body = unwrapData(res);
        const skuMap: Record<string, any> = body?.["product-sku"] || {};
        const categoryVariantSection: Record<string, Array<any>> = body?.["category-variant"] || {};
        const optionValueLookup = buildOptionValueLookup(categoryVariantSection);
        const rows = Object.values(skuMap || {}).map((sku: any) => ({
          skuId: sku.skuId,
          skuCode: sku.skuCode || "",
          price: sku.price ?? 0,
          stock: sku.stock ?? 0,
          isActive: !!sku.isActive,
          combo: buildSkuCombo(sku, optionValueLookup),
        }));
        setSkuRows(rows);
      })
      .catch((err: any) => {






        const message: string = getErrorMessage(err, "");
        const looksLikeNotFound =
          err?.response?.status === 400 ||
          err?.response?.status === 404 ||
          /not found/i.test(message);
        if (looksLikeNotFound) {
          setSkuRows([]);
        } else {
          setSkuLoadError(true);
        }
      })
      .finally(() => setSkuLoading(false));
  }, [isVariable, product.productId, product.categoryId]);

  useEffect(() => {
    if (viewOpen)
      loadSkus();
  }, [viewOpen]);

  useEffect(() => {
    return productEvents.onProductChanged((changedProductId) => {
      if (changedProductId === product.productId && viewOpen) {
        loadSkus();
      }
    });
  }, [product.productId, viewOpen, loadSkus]);

  const handleDelete = () => {
    setDeleting(true);
    ProductService.deleteProduct(product.productId)
      .then(() => {
        setSnackbar({ open: true, message: "Product deleted.", severity: "success" });
        setConfirmOpen(false);
        onDeleted?.();
      })
      .catch((err: any) => {
        setSnackbar({
          open: true,
          message: getErrorMessage(err, "Could not delete this product."),
          severity: "error",
        });
      })
      .finally(() => setDeleting(false));
  };

  const updateStatus = (nextStatus: number, onDone?: () => void) => {
    setStatusUpdating(true);
    ProductService.updateProduct(product.productId, { ...product, estatusId: nextStatus })
      .then(() => {
        productEvents.emitProductChanged(product.productId);
        onDone?.();
      })
      .catch((err: any) => {
        setSnackbar({
          open: true,
          message: getErrorMessage(err, "Could not update the publish status."),
          severity: "error",
        });
      })
      .finally(() => setStatusUpdating(false));
  };

  const handleToggleStatus = () => {
    const nextStatus = isPublished ? PRODUCT_STATUS.DRAFT : PRODUCT_STATUS.PUBLISHED;
    updateStatus(nextStatus, () => {
      setSnackbar({
        open: true,
        message: isPublished ? "Product unpublished." : "Product published.",
        severity: "success",
      });
    });
  };

  const handleUnpublishAndEdit = () => {
    updateStatus(PRODUCT_STATUS.DRAFT, () => {
      setUnpublishToEditOpen(false);
      navigate(`/products/add-product/${product.productId}`);
    });
  };

  const description = toPlainText(product.description);
  const fullDescription = toPlainText((product as any).product_detail);

  return (
    <Box>
      <Tooltip title="View Product details">
        <IconButton onClick={() => setViewOpen(true)} sx={{ color: theme.palette.secondary.main }}>
          <PreviewIcon />
        </IconButton>
      </Tooltip>
      {isPublished ? (
        <Tooltip title="Published — unpublish to edit">
          <IconButton sx={{ color: theme.palette.grey?.[500] || "grey" }} onClick={() => setUnpublishToEditOpen(true)}>
            <LockOutlinedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Edit Product details">
          <Link to={`/products/add-product/${product.productId}`}>
            <IconButton sx={{ color: theme.palette.primary.main }}>
              <EditIcon />
            </IconButton>
          </Link>
        </Tooltip>
      )}
      <Tooltip title={isPublished ? "Unpublish Product" : "Publish Product"}>
        <span>
          <IconButton
            disabled={statusUpdating}
            onClick={handleToggleStatus}
            sx={{ color: isPublished ? "#f1b44c" : theme.status.success }}
          >
            {isPublished ? <UnpublishedRoundedIcon /> : <PublishedWithChangesRoundedIcon />}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Delete Product">
        <IconButton onClick={() => setConfirmOpen(true)} sx={{ color: theme.status.danger }}>
          <DeleteIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #FDB44D 100%)`,
            color: "#fff",
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Avatar
            variant="rounded"
            sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 48, height: 48 }}
          >
            <InventoryIcon />
          </Avatar>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="h6" fontWeight="bold" noWrap>
              {product.title || "Untitled Product"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Product ID: {product.productId}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={isPublished ? "Published" : "Draft"}
            sx={{
              bgcolor: isPublished ? "rgba(52,195,143,0.9)" : "rgba(241,180,76,0.9)",
              color: "#fff",
              fontWeight: 600,
            }}
          />
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          <Box display="flex" gap={1.5} flexWrap="wrap" mb={2.5}>
            <Chip
              label={`Rs ${product.price ?? 0}`}
              sx={{ bgcolor: theme.palette.primary.light, color: theme.palette.primary.main, fontWeight: 600 }}
            />
            <Chip
              label={`Stock: ${product.stock ?? 0}`}
              variant="outlined"
              color={Number(product.stock) > 0 ? "success" : "error"}
            />
            {product.product_type && (
              <Chip label={product.product_type} variant="outlined" sx={{ textTransform: "capitalize" }} />
            )}
            {product.brand && <Chip label={product.brand} variant="outlined" />}
          </Box>

          <Typography variant="overline" color="text.secondary">Short Description</Typography>
          <Typography variant="body1" sx={{ whiteSpace: "pre-line", mb: 2.5 }}>
            {description || "—"}
          </Typography>

          {fullDescription && (
            <>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="overline" color="text.secondary">Full Description</Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-line", color: "text.secondary" }}>
                {fullDescription}
              </Typography>
            </>
          )}

          {isVariable && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" alignItems="center" spacing={0.75} mb={1}>
                <TuneRoundedIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                <Typography variant="overline" color="text.secondary">Variants</Typography>
              </Stack>

              {skuLoading && (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={22} />
                </Box>
              )}

              {!skuLoading && skuLoadError && (
                <Typography variant="body2" color="error">
                  Could not load variants. Please try again.
                </Typography>
              )}

              {!skuLoading && !skuLoadError && skuRows.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No SKUs have been saved for this product yet. Add them from Edit Product → Variants tab.
                </Typography>
              )}

              {!skuLoading && !skuLoadError && skuRows.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Combination</TableCell>
                      <TableCell>SKU Code</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Stock</TableCell>
                      <TableCell align="center">Active</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {skuRows.map((row) => (
                      <TableRow key={row.skuId}>
                        <TableCell>{row.combo}</TableCell>
                        <TableCell>{row.skuCode || "-"}</TableCell>
                        <TableCell>Rs {row.price}</TableCell>
                        <TableCell>{row.stock}</TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={row.isActive ? "Active" : "Inactive"}
                            color={row.isActive ? "success" : "default"}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setViewOpen(false)} variant="outlined">Close</Button>
          {isPublished ? (
            <Button
              variant="contained"
              startIcon={<LockOutlinedIcon />}
              onClick={() => {
                setViewOpen(false);
                setUnpublishToEditOpen(true);
              }}
            >
              Unpublish to Edit
            </Button>
          ) : (
            <Button
              component={Link}
              to={`/products/add-product/${product.productId}`}
              variant="contained"
              startIcon={<EditIcon />}
            >
              Edit Product
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={unpublishToEditOpen}
        onClose={() => setUnpublishToEditOpen(false)}
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <DialogTitle>Unpublish to Edit</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{product.title}" is currently published, so it can't be edited directly. Unpublish it now to make
            changes? It will go back to Draft and stop showing to customers until you publish it again.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUnpublishToEditOpen(false)} disabled={statusUpdating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleUnpublishAndEdit} disabled={statusUpdating}>
            {statusUpdating ? "Please wait..." : "Unpublish & Edit"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{product.title}"? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductActions;
